import React from 'react';
import { SpeakerWaveIcon } from '@heroicons/react/24/outline';
import { PauseIcon, PlayIcon } from '@heroicons/react/24/solid';

type NarrationControlsProps = {
  isSupported: boolean;
  narrationEnabled: boolean;
  isMuted: boolean;
  onToggle: () => void;
  onMute: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

const NarrationControls: React.FC<NarrationControlsProps> = ({
  isSupported,
  narrationEnabled,
  isMuted,
  onToggle,
  onMute,
}) => {
  if (!isSupported) {
    return (
      <div className="w-full rounded-full border border-white/10 bg-white/5 text-[10px] uppercase tracking-[0.4em] text-white/35 py-3 text-center">
        Narration Unavailable
      </div>
    );
  }

  return (
    <div className="w-full flex items-center gap-3">
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onToggle();
        }}
        className={`flex-1 relative flex items-center justify-center text-[11px] uppercase tracking-[0.35em] px-3 py-3 rounded-full border transition ${
          narrationEnabled
            ? 'border-emerald-200 text-emerald-100 bg-emerald-300/20 shadow-[0_0_12px_rgba(16,185,129,0.35)]'
            : 'border-white/25 text-white/65 hover:text-white hover:border-white/50'
        }`}
        aria-pressed={narrationEnabled}
      >
        <span className="flex items-center justify-center gap-3">
          <span className="w-7 h-7 rounded-full bg-emerald-300/20 border border-emerald-200/60 flex items-center justify-center shadow-[0_0_10px_rgba(16,185,129,0.5)]">
            {narrationEnabled ? (
              <PauseIcon className="w-4 h-4 text-emerald-100" />
            ) : (
              <PlayIcon className="w-4 h-4 text-emerald-100" />
            )}
          </span>
          <span>{narrationEnabled ? 'Pause Story' : 'Play Story'}</span>
        </span>
      </button>
      <button
        type="button"
        onClick={onMute}
        disabled={!narrationEnabled}
        className={`w-10 h-10 rounded-full border text-[9px] tracking-[0.2em] flex items-center justify-center transition ${
          !narrationEnabled
            ? 'border-white/10 text-white/30 bg-white/5 cursor-not-allowed'
            : isMuted
            ? 'border-emerald-200 text-emerald-100 bg-emerald-300/30'
            : 'border-white/30 text-white/70 hover:text-white hover:border-white/60'
        }`}
        aria-pressed={isMuted}
        aria-disabled={!narrationEnabled}
        aria-label={isMuted ? 'Unmute narration' : 'Mute narration'}
      >
        <span className="relative w-4 h-4 flex items-center justify-center">
          <SpeakerWaveIcon className="w-4 h-4" />
          {isMuted && (
            <span className="absolute left-0 top-1/2 w-4 h-[2px] bg-current rotate-45 rounded-full" />
          )}
        </span>
      </button>
    </div>
  );
};

export default NarrationControls;
